import React, { useMemo, useState } from 'react';
import { FormikHelpers } from 'formik';
import { useGetTeleprompterScripts } from 'lib/api/teleprompter/getScripts';
import { useEditTeleprompterScriptMutation } from 'lib/api/teleprompter/useEditTeleprompterScriptMutation';
import { useCreateTeleprompterScriptMutation } from 'lib/api/teleprompter/useCreateTeleprompterScriptMutation';
import { FormAction, ICreateSpotlightFormikValues } from './types';
import { CREATE_NEW_SCRIPT_ID, defaultOptions } from './constants';
import { TeleprompterResponse } from 'lib/api/teleprompter/types';
import { useQueryBackgrounds } from 'lib/api/backgrounds/useQueryBackgrounds';
import { AddBackgroundModal } from '../../aiAssistants/backgrounds/modals/AddBackgroundModal';
import { useCreateSpotlightMutation } from 'lib/api/spotlight/useCreateSpotlightMutation';
import { errorToast } from 'lib/components/toasts/error';
import { AISpotlightForm } from './forms/AISpotlightForm';
import { useHistory } from 'react-router';
import { mapFolderToOption } from 'lib/utils/folders';
import { DEFAULT_FOLDER } from 'lib/const/Folders';
import { useAuth } from 'lib/context';
interface ICreateSpotlightModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const SCRIPT_MAX_LENGTH = 1500;

export const CreateAISpotlightModal = ({
  isOpen,
  onClose,
}: ICreateSpotlightModalProps) => {
  const history = useHistory();
  const { userData } = useAuth();
  const [isUpsertingScript, setIsUpsertingScript] = useState(false);
  const [showBackgroundUploadModal, setShowBackgroundUploadModal] =
    useState(false);

  const toggleShowBackgroundUploadModal = () => {
    setShowBackgroundUploadModal(prev => !prev);
  };

  // Fetch existing scripts
  const {
    data: scriptData,
    isLoading: isLoadingScripts,
    isFetching: isFetchingScripts,
  } = useGetTeleprompterScripts({});
  const scripts = scriptData?.scripts || [];
  // Update existing script
  const {
    mutateAsync: editTeleprompterScriptMutation,
    isLoading: loadingEditTeleprompterScript,
  } = useEditTeleprompterScriptMutation();
  // Create new script
  const {
    mutateAsync: createTeleprompterScriptMutation,
    isLoading: loadingCreateTeleprompterScript,
  } = useCreateTeleprompterScriptMutation();
  // Fetch backgrounds
  const {
    data: backgrounds,
    isLoading: isLoadingBackgrounds,
    isFetching: isFetchingBackgrounds,
  } = useQueryBackgrounds();
  const defaultBackgroundId =
    backgrounds?.find(bg => !!bg.isGlobalDefault)?.backgroundId || 0;
  // Create AI Spotlight modal
  const onCreateSpotlightSuccess = (uploadedVideoId: number) => {
    const targetFolder = mapFolderToOption(DEFAULT_FOLDER, userData.id);
    history.push({
      pathname: '/review-videos',
      state: {
        videos: [uploadedVideoId],
        targetFolder,
        title: 'Add AI Spotlight',
        showCounter: false,
        saveText: 'Save Spotlight',
      },
    });
    onClose();
  };
  const { mutateAsync: createSpotlightMutation } = useCreateSpotlightMutation(
    onCreateSpotlightSuccess
  );

  const isPreparingData = isLoadingScripts || isLoadingBackgrounds;
  const isLoading =
    isLoadingScripts ||
    isLoadingBackgrounds ||
    loadingEditTeleprompterScript ||
    loadingCreateTeleprompterScript ||
    isUpsertingScript;

  const combinedOptions: TeleprompterResponse[] = useMemo(() => {
    const options = [...scripts].sort(
      (a, b) => Number(a.sharedWithAll) - Number(b.sharedWithAll)
    );
    return [...defaultOptions, ...options];
  }, [scriptData]);

  const handleUpdateExistingScript = async (
    values: ICreateSpotlightFormikValues,
    setFieldValue: (field: string, value: any) => void
  ) => {
    setIsUpsertingScript(true);
    const { scriptId, text } = values;
    const content = encodeURIComponent(text || '');

    const res = await editTeleprompterScriptMutation({
      scriptId: scriptId?.toString() || '',
      data: { content },
    });
    if (res && res.scriptId) {
      setFieldValue('scriptId', Number(res.scriptId));
    }
  };

  const handleCreateNewScript = async (
    values: ICreateSpotlightFormikValues,
    setFieldValue: (field: string, value: any) => void
  ) => {
    setIsUpsertingScript(true);

    const { text, title } = values;
    const content = encodeURIComponent(text || '');
    const newScriptData = {
      title: title,
      content,
    };
    const res = await createTeleprompterScriptMutation({
      data: newScriptData,
    });
    if (res && res.scriptId) {
      setFieldValue('scriptId', Number(res.scriptId));
    }
  };

  const onSave = async (
    values: ICreateSpotlightFormikValues,
    { setFieldValue }: FormikHelpers<ICreateSpotlightFormikValues>
  ) => {
    setIsUpsertingScript(true);
    if (values.action === FormAction.SUBMIT) {
      if (values.backgroundId === 0) {
        errorToast({ title: 'Please select a background before proceeding.' });
        setIsUpsertingScript(false);
        return;
      }
      const submitValues = {
        script: values.text,
        backgroundId: values.backgroundId?.toString(),
      };
      await createSpotlightMutation(submitValues);
    } else if (values.action === FormAction.SCRIPT_UPSERT) {
      if (values.scriptId === CREATE_NEW_SCRIPT_ID) {
        await handleCreateNewScript(values, setFieldValue);
        setIsUpsertingScript(false);
        return;
      }
      await handleUpdateExistingScript(values, setFieldValue);
    }
    setIsUpsertingScript(false);
  };

  if (!isOpen) {
    return null;
  }

  return (
    <>
      <AISpotlightForm
        onSave={onSave}
        onClose={onClose}
        isLoading={isLoading}
        options={combinedOptions}
        backgrounds={backgrounds || []}
        defaultBackgroundId={defaultBackgroundId}
        isPreparingData={isPreparingData}
        toggleShowBackgroundUploadModal={toggleShowBackgroundUploadModal}
        isFetchingBackgrounds={isFetchingBackgrounds}
        isFetchingScripts={isFetchingScripts}
      />
      <AddBackgroundModal
        isOpen={showBackgroundUploadModal}
        onClose={toggleShowBackgroundUploadModal}
      />
    </>
  );
};
